import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { InstallApp } from "@/components/InstallReadingApp";

const field = "w-full rounded-2xl border-[3px] border-slate-900 bg-white px-4 py-3 text-lg font-semibold outline-none focus:ring-4 focus:ring-blue-300";

function AppLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user, data.parent_unlock);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-8 w-full max-w-sm space-y-3" data-testid="reading-app-login">
      <p className="font-bold">Parents : connectez-vous · Parents: sign in</p>
      <input className={field} type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reading-app-email" />
      <input className={field} type="password" required placeholder="Mot de passe · Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reading-app-password" />
      <button disabled={busy} className="w-full rounded-full bg-blue-600 text-white border-[3px] border-slate-900 py-3 text-lg font-black shadow-[4px_4px_0_0_#0F172A] disabled:opacity-60" data-testid="reading-app-login-btn">
        {busy ? <Loader2 className="animate-spin mx-auto" /> : "Se connecter · Sign in"}
      </button>
      <Link to="/forgot-password" className="block text-sm underline" data-testid="reading-app-forgot">Mot de passe oublié ? · Forgot password?</Link>
      <p className="text-sm text-slate-600">Same account as the Anou Appran website. · Le même compte que sur le site Anou Appran.</p>
    </form>
  );
}

const access = (b) => [b?.reading_included || b?.reading_paid?.includes("en") ? "English" : null, b?.reading_included || b?.reading_paid?.includes("fr") ? "Français" : null].filter(Boolean);

function ChildPicker() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState(null);
  const [bill, setBill] = useState({});
  useEffect(() => {
    api.get("/children").then(({ data }) => setKids(data.children)).catch(() => setKids([]));
    api.get("/billing").then(({ data }) => setBill(Object.fromEntries(data.children.map((c) => [c.id, c])))).catch(() => {});
  }, []);
  if (!kids) return <Loader2 className="animate-spin mt-10" size={36} />;
  return (
    <div className="mt-8 w-full max-w-xl">
      <h2 className="text-2xl font-black">Qui lit aujourd'hui ? · Who is reading?</h2>
      {!kids.length && <p className="mt-4" data-testid="reading-app-no-kids">No child on this account yet. · Aucun enfant sur ce compte.</p>}
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {kids.map((k, i) => (
          <motion.button key={k.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            onClick={() => navigate(`/kid/${k.id}/reading`)} data-testid={`reading-app-child-${k.id}`}
            className="flex items-center gap-4 rounded-3xl border-[3px] border-slate-900 bg-white p-4 text-left shadow-[4px_4px_0_0_#0F172A] hover:-translate-y-1 transition-transform">
            <Avatar avatar={k.avatar} img={k.avatar_img} size={64} />
            <span>
              <b className="text-xl block">{k.name}</b>
              <span className="text-sm text-slate-600">Grade {k.grade}</span>
              <span className="block text-xs font-bold mt-1 text-green-700" data-testid={`reading-app-child-access-${k.id}`}>{access(bill[k.id]).join(" · ") || "Essai · Try a free lesson"}</span>
            </span>
          </motion.button>
        ))}
      </div>
      <button onClick={logout} className="mt-8 inline-flex items-center gap-2 text-sm font-bold underline" data-testid="reading-app-logout"><LogOut size={16} /> Se déconnecter · Log out</button>
    </div>
  );
}

export default function ReadingApp() {
  const { user, loading } = useAuth();
  return (
    <div className="min-h-screen bg-[#FEF9C3] font-kids flex flex-col items-center px-5 py-10 text-slate-900" data-testid="reading-app">
      <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-28 h-28" animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 1.6 }} />
      <h1 className="text-4xl sm:text-5xl font-black text-center mt-2">Anou Appran Lecture</h1>
      <p className="mt-2 text-lg font-semibold text-center">Le kit Lecture avec Kiko · Reading with Kiko — English & Français</p>
      <InstallApp />
      {loading ? <Loader2 className="animate-spin mt-10" size={36} /> : user ? <ChildPicker /> : <AppLogin />}
      <p className="mt-auto pt-12 text-xs text-slate-500 text-center" data-testid="reading-app-copyright">
        © {new Date().getFullYear()} Anou Appran. Tous droits réservés · All rights reserved. Copying or sharing the content is not allowed.
      </p>
    </div>
  );
}
